/**
 * mentionAffordance — the one-keystroke @mention helper for gated threads
 * (owner directive 2026-08-11).
 *
 * In a group thread that requires an @Prest0n mention, addressing anyone is a
 * typing chore the product itself created. This module decides, purely from
 * thread state, WHO a single Tab press (or one click on the composer chip)
 * should mention:
 *
 *   • Prest0n, when it sent the latest message — the user is most plausibly
 *     continuing with Prest0n and the gate would otherwise silence them; also
 *     the default when the thread is empty or the last sender is the user
 *     themself (they are most plausibly summoning Prest0n next).
 *   • The HUMAN who sent the latest message otherwise — replying to the person
 *     who just spoke is the natural next act in a group conversation.
 *
 * Returns null whenever the affordance would be noise: the thread doesn't
 * gate, the requirement is relaxed, or the draft already carries the target's
 * mention. Pure and dependency-light (predicates + handle derivation only) so
 * the rule unit-tests without React.
 */

import { mentionsAssistant } from './summonGate';
import { ASSISTANT_MENTION } from './suggestionMention';
import { deriveMentionHandle } from './mentionAutocomplete';
import type { MentionRosterEntry } from './mentionResolver';

/** Sentinel senderId the shared store uses for assistant turns. */
const ASSISTANT_SENDER_ID = 'assistant';

export interface MentionAffordanceMessage {
  isUser?: boolean;
  isSystem?: boolean;
  senderId?: string | null;
}

export interface MentionAffordanceTarget {
  /** Plain handle to insert after '@' (no '@' included), e.g. 'Prest0n'. */
  handle: string;
  /** Display label for the chip, e.g. '@Prest0n'. */
  display: string;
  kind: 'assistant' | 'member';
}

const ASSISTANT_TARGET: MentionAffordanceTarget = {
  handle: ASSISTANT_MENTION.slice(1),
  display: ASSISTANT_MENTION,
  kind: 'assistant',
};

/** The latest non-system message, or null. */
function lastRealMessage(
  messages: ReadonlyArray<MentionAffordanceMessage> | undefined,
): MentionAffordanceMessage | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.isSystem !== true) return m;
  }
  return null;
}

export function resolveMentionAffordance(args: {
  /** The active thread actually gates (summon flag on + ≥2 humans). */
  gatesThisThread: boolean;
  /** The thread's effective mention requirement. */
  requireMention: boolean | null | undefined;
  /** Current composer draft (plain text). */
  draftText: string | null | undefined;
  /** Thread messages, oldest first (only the tail is inspected). */
  messages: ReadonlyArray<MentionAffordanceMessage> | undefined;
  /** The current user's uid. */
  selfId?: string | null;
  /** Account roster for handle derivation (absent ⇒ assistant fallback). */
  roster?: ReadonlyArray<MentionRosterEntry>;
}): MentionAffordanceTarget | null {
  if (!args.gatesThisThread || args.requireMention === false) return null;

  const draft = typeof args.draftText === 'string' ? args.draftText : '';

  const last = lastRealMessage(args.messages);
  const lastIsAssistant = !last || last.isUser !== true || last.senderId === ASSISTANT_SENDER_ID;
  const lastSender = (last?.senderId ?? '').trim();
  const self = (args.selfId ?? '').trim();

  if (lastIsAssistant || !lastSender || lastSender === self) {
    // Summon Prest0n — unless the draft already does.
    if (mentionsAssistant(draft)) return null;
    return ASSISTANT_TARGET;
  }

  // A human teammate spoke last — offer their mention.
  const roster = args.roster || [];
  const member = roster.find((m) => (m.userId ?? '').trim() === lastSender);
  if (!member) {
    // Unknown sender (roster not loaded / departed member): fall back to the
    // assistant target so Tab always does something useful in a gated thread.
    if (mentionsAssistant(draft)) return null;
    return ASSISTANT_TARGET;
  }
  const handle = deriveMentionHandle(member, roster);
  if (!handle) {
    if (mentionsAssistant(draft)) return null;
    return ASSISTANT_TARGET;
  }
  if (draft.toLowerCase().includes(`@${handle.toLowerCase()}`)) return null;
  return { handle, display: `@${handle}`, kind: 'member' };
}
